/**
 * Modulo que encapsula los formateadores de fechas.
 * Tiene formatos predeterminados en función del DATEFORMATS eligido.
 */

/**
 * ## documentación
 *
 * https://www.npmjs.com/package/date-fns-tz
 *
 * ## Sin timezone
 *
 * Estas funciones no toman en cuenta el `edicom.locale.timezone`.
 * Para eso, mirar `edicom.locale.tz`.
 */
//

import { format as dateFnsFormat } from 'date-fns-tz';
import { onBeforeFormat } from './src/onBeforeFormat';

/**
 * Devuelve una fecha en string formateada.
 * En caso de errores, devuelve un string vacio para que no aparezca "Invalid date" o lo que sea al usuario.
 *
 * @param {Object} state
 * @param {string|number|Date} value
 * @param {string|Object} dateFormat el formateo, los valores posibles son los valores del DATEFORMATS datetime|datetimelong|date|time|timelong
 * @returns {string}
 */
export function formatFn(state, value, dateFormat) {
  if (!value) {
    // por si value es null o undefined
    return '';
  }

  try {
    // parseamos la fecha (que puede venir en {string|number|Date|null|undefined})
    const currentDate = onBeforeFormat(value);

    // si el dateFormat es de tipo string, usamos `Date-fns` p ara parsearlo
    // OJO que no soporta los tipos localizados, ej 'Pp' ...
    if (typeof dateFormat === 'string') {
      // Usamos el `format` del date-fns-tz que encapsula el `format` del date-fns.
      // la opción `timeZone` solo sirve por si el formato usa: z..zzzz, x..xxxxx, X..XXXXX and O..OOO
      // https://date-fns.org/v2.23.0/docs/Time-Zones
      // https://www.npmjs.com/package/date-fns-tz
      return dateFnsFormat(currentDate, dateFormat, { timeZone: state.timezone });
    }

    // si el dateFormat es de tipo object, usamos el objeto nativo `Intl.DateTimeFormat`
    // nos ahora tener que cargar locale
    if (typeof dateFormat === 'object') {
      if (!state.cache?.DateTimeFormat?.[state.id]?.has(dateFormat)) {
        state.cache = state.cache ?? {};
        state.cache.DateTimeFormat = state.cache.DateTimeFormat ?? {};
        state.cache.DateTimeFormat[state.id] = state.cache.DateTimeFormat[state.id] ?? new WeakMap();
        state.cache.DateTimeFormat[state.id].set(dateFormat, new Intl.DateTimeFormat(state.id, dateFormat));
      }
      const formatter = state.cache.DateTimeFormat[state.id].get(dateFormat);
      return formatter.format(currentDate);
    }
  } catch (e) {
    console.error(e);
  }
  return '';
}
